import React, {useState, useEffect} from "react";
import {Card} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import {apiClient} from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './WinksComponent.css';
import Badge from "react-bootstrap/Badge";
import PaginationComponent from "../pagingResults/PaginationComponent";
import { Trash2 } from 'lucide-react';

const WinksComponent = () => {
  const [winks, setWinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  
  const navigate = useNavigate();
  const {fastPhotos, loading: fastPhotosLoading, error: fastPhotosError} = useFastPhotos();

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  const fetchWinks = async () => {
    setLoading(true);
    try {
      // Use the updated paginated endpoint
      const response = await apiClient.get(`/rest/winks?page=${currentPage}&size=${itemsPerPage}`);
      
      if (response.status === 200) {
        console.log('Winks response:', response.data);
        
        // Handle Spring Boot Page object
        if (response.data.content) {
          // If the response is a Page object
          setWinks(response.data.content);
          setTotalElements(response.data.totalElements);
        } else {
          // Fallback for backward compatibility
          const validData = response.data.filter(item => Array.isArray(item) && item.length > 1);
          setWinks(validData);
          setTotalElements(validData.length);
        }
      } else {
        setError('Failed to fetch winks');
      }
    } catch (error) {
      console.error('Error fetching winks:', error);
      setError('Error fetching winks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Fetching winks - Page:', currentPage, 'Size:', itemsPerPage);
    fetchWinks();
  }, [currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle click on a wink profile
  const handleWinkProfileClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Updated to work with new WinkEndpoints API
  const handleDeleteWink = async (winkId) => {
    try {
      // New API format matching WinkEndpoints.java
      const winkRequest = {
        wink: [
          {
            name: "wink_delete_list",
            val: winkId.toString()
          }
        ]
      };
      
      const response = await apiClient.post('/rest/deleteWinksReceived', winkRequest);
      
      if (response.status === 200) {
        console.log('Wink deleted successfully:', response.data);
        // Refresh data after deletion
        fetchWinks();
      }
    } catch (error) {
      console.error('Error removing wink:', error);
    }
  };

  return (
    <div className="container-1024">
      <div className="results-area">
        <h3>My Winks</h3>
        <div className="results-content">
          <div className="results-center-details">
            <div className="w-100 mb-3">
              <PaginationComponent
                totalItems={totalElements}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setCurrentPage(0); // Reset to first page when changing size
                }}
              />
            </div>

            {loading ? (
              <p>Loading winks...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="card-grid">
                {winks.length > 0 ? (
                  winks.map((wink, index) => (
                    <Card key={index} className="favorite-card position-relative">
                      <div className="delete-icon-wrapper"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1,
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '50%',
                          padding: '5px'
                        }}>
                        <Trash2
                          size={20}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteWink(wink[0]);
                          }}
                        />
                      </div>
                      {/* Changed from Link to a onClick handler to record view */}
                      <a 
                        href={`/profile/view/1/${wink[1]}`} 
                        className="favorite-card-link"
                        onClick={(e) => handleWinkProfileClick(e, wink[1])}
                      >
                        <Card.Img 
                          variant="top" 
                          src={`https://pics.netfixup.com/${wink[21]}/${wink[20]}`} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                          }}
                        />
                      </a>
                      <Card.Body>
                        <Card.Title>{wink[2]}</Card.Title>
                        <Card.Text>
                          <b>Age:</b> {wink[3]}<br />
                          <b>Gender:</b> {wink[5]}<br />
                          <b>Looking for:</b> {wink[6]}<br />
                          <b>Location:</b> {wink[16]}, {wink[17]}<br />
                          <b>Online: </b> {wink[25]}<br />
                          <b>Winked Date: </b> {formatDate(wink[24])}<br />
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No Winks Found</p>
                )}
              </div>
            )}
          </div>

          <div className="results-right-sidebar">
            <h5>Close Matches</h5>
            {fastPhotosLoading ? (
              <p>Loading...</p>
            ) : fastPhotosError ? (
              <p>{fastPhotosError}</p>
            ) : fastPhotos.length > 0 ? (
              fastPhotos.map((photo, index) => (
                <Card key={index} className="fast-photo-card-side">
                  {/* Changed from Link to a onClick handler to record view */}
                  <a 
                    href={`/profile/view/1/${photo[0]}`}
                    onClick={(e) => handleCloseMatchClick(e, photo[0])}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                      }}
                    />
                  </a>
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </Card>
              ))
            ) : (
              <p>No Fast Photos Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WinksComponent;